import React from "react";
import { API } from "@/lib/api";

const resolveImg = (src) => (src?.startsWith("http") ? src : `${API}/files/${src}`);

// Inline formatting: **bold**, [text](url)
function renderInline(text, keyPrefix = "i") {
  const nodes = [];
  let remaining = text;
  let k = 0;
  const pattern = /(\*\*([^*]+)\*\*)|(\[([^\]]+)\]\(([^)]+)\))/;
  let m;
  while ((m = remaining.match(pattern))) {
    const idx = m.index;
    if (idx > 0) nodes.push(<React.Fragment key={`${keyPrefix}-t${k++}`}>{remaining.slice(0, idx)}</React.Fragment>);
    if (m[1]) {
      nodes.push(<strong key={`${keyPrefix}-b${k++}`}>{m[2]}</strong>);
    } else if (m[3]) {
      const href = m[5];
      const external = /^https?:\/\//.test(href);
      nodes.push(
        <a key={`${keyPrefix}-l${k++}`} href={href} className="text-primary underline underline-offset-2 hover:opacity-80"
           {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
          {m[4]}
        </a>
      );
    }
    remaining = remaining.slice(idx + m[0].length);
  }
  if (remaining) nodes.push(<React.Fragment key={`${keyPrefix}-t${k++}`}>{remaining}</React.Fragment>);
  return nodes;
}

export default function RichContent({ content = "", className = "" }) {
  if (!content) return null;
  const blocks = content.replace(/\r\n/g, "\n").split(/\n\s*\n/);

  return (
    <div className={`space-y-5 leading-relaxed text-[15px] text-foreground/90 ${className}`} data-testid="rich-content">
      {blocks.map((raw, bi) => {
        const block = raw.trim();
        if (!block) return null;
        const lines = block.split("\n");

        // CTA button block: [[button:Label|url]]
        const btn = block.match(/^\[\[button:([^|]+)\|([^\]]+)\]\]$/);
        if (btn) {
          const href = btn[2].trim();
          const external = /^https?:\/\//.test(href);
          return (
            <div key={bi} className="py-2">
              <a href={href} data-testid="content-cta-button"
                 {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                 className="btn-primary inline-flex items-center px-6 py-3 text-sm rounded-sm">
                {btn[1].trim()}
              </a>
            </div>
          );
        }

        // Image-only block: ![alt](src)
        const img = block.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
        if (img) {
          return (
            <figure key={bi} className="my-6">
              <img src={resolveImg(img[2].trim())} alt={img[1]} className="w-full rounded-sm border border-border" />
            </figure>
          );
        }

        // Headings
        if (/^###\s+/.test(block) && lines.length === 1)
          return <h3 key={bi} className="font-serif text-xl mt-6">{renderInline(block.replace(/^###\s+/, ""), `h3${bi}`)}</h3>;
        if (/^##\s+/.test(block) && lines.length === 1)
          return <h2 key={bi} className="font-serif text-2xl mt-8">{renderInline(block.replace(/^##\s+/, ""), `h2${bi}`)}</h2>;

        // Bullet list
        if (lines.every((l) => /^[-*]\s+/.test(l.trim()))) {
          return (
            <ul key={bi} className="list-disc pl-6 space-y-1.5 marker:text-primary">
              {lines.map((l, li) => <li key={li}>{renderInline(l.trim().replace(/^[-*]\s+/, ""), `ul${bi}-${li}`)}</li>)}
            </ul>
          );
        }
        // Numbered list
        if (lines.every((l) => /^\d+\.\s+/.test(l.trim()))) {
          return (
            <ol key={bi} className="list-decimal pl-6 space-y-1.5 marker:text-primary">
              {lines.map((l, li) => <li key={li}>{renderInline(l.trim().replace(/^\d+\.\s+/, ""), `ol${bi}-${li}`)}</li>)}
            </ol>
          );
        }

        // Paragraph (supports soft line breaks)
        return (
          <p key={bi}>
            {lines.map((l, li) => (
              <React.Fragment key={li}>
                {renderInline(l, `p${bi}-${li}`)}
                {li < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
